import cn from 'classnames'
import type { GrowthCategory } from '@mc/api'
import {
  Button,
  Beta,
  IconChevronRightDefault,
  IconChevronLeftDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import Link from 'components/Link'
import { CatButtonTwoPageQRExperiment } from 'experiments/pen-5375_v2_two_page_qr'
import styles from './CategoryCarousel.module.scss'

interface CategoryCarouselProps {
  categories: GrowthCategory[]
  className?: string
  onCategoryClick: (category: GrowthCategory) => void
  asButton?: boolean
  hasControl?: boolean
  getPathFunction: (slug: string) => string
  selectedCategory?: string
  loop?: boolean
  overflow?: boolean // pen-5375_v2_two_page_qr
}

const CategoryCarousel = ({
  categories,
  className = 'mc-py-4',
  onCategoryClick,
  asButton,
  hasControl = true,
  getPathFunction,
  selectedCategory = '',
  loop = true,
  overflow = false, // pen-5375_v2_two_page_qr
}: CategoryCarouselProps) => {
  const { t } = useTranslation('@mc/next')

  const handleClick = (category: GrowthCategory) => {
    onCategoryClick(category)
  }

  if (!categories) return <></>

  return (
    <nav className={cn('mc-overflow--hidden', className)}>
      <div className={cn({ container: hasControl })}>
        <Beta.Carousel srLabel={t('categories.a11y.categories')} loops={loop}>
          <div className='d-flex align-items-center'>
            {hasControl && (
              <Beta.CarouselPrevious kind='link' size='xs' symmetrical rounded>
                <IconChevronLeftDefault />
              </Beta.CarouselPrevious>
            )}

            <Beta.CarouselContent overflow={overflow}>
              {categories.map((category) => (
                <Beta.CarouselItem key={category.slug}>
                  {asButton ? (
                    <CatButton
                      category={category}
                      selectedCategory={selectedCategory}
                      onClick={handleClick}
                    />
                  ) : (
                    <CatLink
                      category={category}
                      selectedCategory={selectedCategory}
                      onClick={handleClick}
                      getPathFunction={getPathFunction}
                    />
                  )}
                </Beta.CarouselItem>
              ))}
            </Beta.CarouselContent>

            {hasControl && (
              <Beta.CarouselNext kind='link' size='xs' symmetrical rounded>
                <IconChevronRightDefault />
              </Beta.CarouselNext>
            )}
          </div>
        </Beta.Carousel>
      </div>
    </nav>
  )
}

type CategoryButtonProp = {
  category: GrowthCategory
  onClick: (cat: GrowthCategory) => void
}

const CatButton = ({
  category,
  selectedCategory,
  onClick,
}: Pick<CategoryCarouselProps, 'selectedCategory'> & CategoryButtonProp) => {
  const { t } = useTranslation('@mc/next')

  return (
    <CatButtonTwoPageQRExperiment
      category={category}
      selectedCategory={selectedCategory}
      onClick={onClick}
      control={
        <Button
          className={cn(
            styles.tab,
            { [styles.tabActive]: selectedCategory === category.slug },
            'mc-p-2',
          )}
          key={category.slug as string}
          kind='link'
          onClick={() => onClick(category)}
        >
          {category.name}
          <span className='mc-sr-only'>
            {t('categories.a11y.category')}
            {selectedCategory === category.slug &&
              t('categories.a11y.selected')}
          </span>
        </Button>
      }
    />
  )
}

const CatLink = ({
  category,
  selectedCategory,
  onClick,
  getPathFunction,
}: Pick<CategoryCarouselProps, 'selectedCategory' | 'getPathFunction'> &
  CategoryButtonProp) => {
  const { t } = useTranslation('@mc/next')

  return (
    <Link
      className={`c-button c-button--${
        selectedCategory === category.slug ? 'secondary' : 'tertiary'
      } c-button--xs mc-mx-1`}
      href={getPathFunction(category.slug as string)}
      key={category.slug}
      onClick={() => onClick(category)}
      passHref
    >
      {category.name}
      <span className='mc-sr-only'>
        {t('categories.a11y.category')}
        {selectedCategory === category.slug && t('categories.a11y.selected')}
      </span>
    </Link>
  )
}

export default CategoryCarousel
